import { useNuxtApp } from "#app";
import { BaseService } from "../base/base.service";
import { ModelConversation, ModelSharedConversationMessageDetail, ModelThumb, ModelThumbFeedbackResponse } from "./dto/conversation.dto";

import { ConversationRepository } from "./conversation.repository";
import { ModelConversationDetail, ModelConversationDetailMessage, ModelPOSTConversationAssociationConversationFile } from "./dto/conversation.detail.dto";
import { ModelResponse, type IModelResponse } from "../base/dto/base.dto";


export class ConversationService extends BaseService {
  private conversationRepository = new ConversationRepository(this.api, this.configService);

  async getConversation(params: any): Promise<IModelResponse<ModelConversation[]>> {
    let dataResult: IModelResponse<ModelConversation[]> = {
      data: [],
    };
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.getConversation(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = (resData.data || []).map(e => new ModelConversation(e));
        dataResult.data = dataList;
        dataResult.totalItems = resData.totalItems || 0;
        console.log('getConversation', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async getConversationDetail(id: string | number): Promise<ModelConversationDetail> {
    let dataResult: ModelConversationDetail = new ModelConversationDetail();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.getConversationDetail(id);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelConversationDetail(resData.data)
        console.log('getConversationDetail', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async createConversation(params: any): Promise<ModelConversationDetail> {
    let dataResult: ModelConversationDetail = new ModelConversationDetail();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.createConversation(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelConversationDetail(resData.data)
        console.log('createConversation', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async putConversation(params: any): Promise<ModelConversationDetail> {
    let dataResult: ModelConversationDetail = new ModelConversationDetail();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.putConversation(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelConversationDetail(resData.data)
        console.log('putConversation', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async *initialConversation(params: any, abortController?: AbortController): AsyncGenerator<any, void, unknown> {
    const { $functions } = useNuxtApp();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let isAborted = false;
    
    try {
      const response = await this.conversationRepository.initialConversation(params, abortController?.signal);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      reader = response.body?.getReader() || null;
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      // Listen for abort signal
      if (abortController?.signal) {
        abortController.signal.addEventListener('abort', () => {
          console.log('[SSE] Abort signal received - canceling reader');
          isAborted = true;
          if (reader) {
            reader.cancel('Stream aborted by user')
              .then(() => console.log('[SSE] Reader canceled successfully'))
              .catch((err) => console.error('[SSE] Reader cancel error:', err));
          }
        });
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        // Check if aborted before reading
        if (isAborted || abortController?.signal.aborted) {
          console.log('[SSE] Stream aborted, breaking loop');
          break;
        }
        
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            continue;
          }
          
          if (line.startsWith('data:')) {
            const data = line.substring(5).trim();
            
            if (data === '[DONE]') {
              return;
            }
            
            try {
              const parsed = JSON.parse(data);
              yield parsed;
            } catch (e) {
              console.warn('Failed to parse SSE data:', data);
            }
          }
        }
      }
    } catch (err: any) {
      console.log('[SSE initialConversation] Stream error:', err);
      // Don't show error notification if it was aborted by user
      if (err.name === 'AbortError') {
        console.log('[SSE initialConversation] Stream aborted by user (AbortError caught)');
      } else {
        $functions.notify.error({
          text: err.message
        });
        console.error(err, ConversationService.name);
      }
    } finally {
      console.log('[SSE initialConversation] Cleaning up reader...');
      // Always cleanup reader
      if (reader) {
        try {
          await reader.cancel();
          console.log('[SSE initialConversation] Reader cleanup done');
        } catch (e) {
          console.log('[SSE initialConversation] Reader cleanup error (ignored):', e);
        }
      }
    }
  }

  async *appendConversation(params: any, abortController?: AbortController): AsyncGenerator<any, void, unknown> {
    const { $functions } = useNuxtApp();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let isAborted = false;
    
    try {
      const response = await this.conversationRepository.appendConversation(params, abortController?.signal);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      reader = response.body?.getReader() || null;
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      // Listen for abort signal
      if (abortController?.signal) {
        abortController.signal.addEventListener('abort', () => {
          console.log('[SSE] Abort signal received - canceling reader');
          isAborted = true;
          if (reader) {
            reader.cancel('Stream aborted by user')
              .then(() => console.log('[SSE] Reader canceled successfully'))
              .catch((err) => console.error('[SSE] Reader cancel error:', err));
          }
        });
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        // Check if aborted before reading
        if (isAborted || abortController?.signal.aborted) {
          console.log('[SSE] Stream aborted, breaking loop');
          break;
        }
        
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            continue;
          }
          
          if (line.startsWith('data:')) {
            const data = line.substring(5).trim();
            
            if (data === '[DONE]') {
              return;
            }
            
            try {
              const parsed = JSON.parse(data);
              yield parsed;
            } catch (e) {
              console.warn('Failed to parse SSE data:', data);
            }
          }
        }
      }
    } catch (err: any) {
      console.log('[SSE appendConversation] Stream error:', err);
      // Don't show error notification if it was aborted by user
      if (err.name === 'AbortError') {
        console.log('[SSE appendConversation] Stream aborted by user (AbortError caught)');
      } else {
        $functions.notify.error({
          text: err.message
        });
        console.error(err, ConversationService.name);
      }
    } finally {
      console.log('[SSE appendConversation] Cleaning up reader...');
      // Always cleanup reader
      if (reader) {
        try {
          await reader.cancel();
          console.log('[SSE appendConversation] Reader cleanup done');
        } catch (e) {
          console.log('[SSE appendConversation] Reader cleanup error (ignored):', e);
        }
      }
    }
  }
  // async appendConversation(params: any): Promise<ModelConversationDetail> {
  //   let dataResult: ModelConversationDetail = new ModelConversationDetail();
  //   const { $functions } = useNuxtApp()
  //   try {
  //     const res = await this.conversationRepository.appendConversation(params);
  //     const resData = res.data.value;
  //     if ($functions.isValidResponse(resData)) {
  //       dataResult = new ModelConversationDetail(resData.data)
  //       console.log('appendConversation', resData.data, dataResult)
  //     }
  //   } catch (err: any) {
  //     $functions.notify.error({
  //       text: err.message
  //     });
  //     console.log(err, ConversationService.name);
  //   }
  //   return dataResult;
  // }

  // async editConversation(params: any): Promise<ModelConversationDetail> {
  //   let dataResult: ModelConversationDetail = new ModelConversationDetail();
  //   const { $functions } = useNuxtApp()
  //   try {
  //     const res = await this.conversationRepository.editConversation(params);
  //     const resData = res.data.value;
  //     if ($functions.isValidResponse(resData)) {
  //       dataResult = new ModelConversationDetail(resData.data)
  //       console.log('editConversation', resData.data, dataResult)
  //     }
  //   } catch (err: any) {
  //     $functions.notify.error({
  //       text: err.message
  //     });
  //     console.log(err, ConversationService.name);
  //   }
  //   return dataResult;
  // }

  async deleteConversation(params: any): Promise<ModelResponse> {
    let dataResult: ModelResponse = new ModelResponse();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.deleteConversation(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = resData;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async conversationAssociationConversationFile(params: any): Promise<ModelPOSTConversationAssociationConversationFile[]> {
    let dataResult: ModelPOSTConversationAssociationConversationFile[] = [];
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.conversationAssociationConversationFile(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = (resData.data || []).map(e => new ModelPOSTConversationAssociationConversationFile(e));
        console.log('conversationAssociationConversationFile', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async thumbFeedback(params: any): Promise<ModelThumbFeedbackResponse> {
    let dataResult: ModelThumbFeedbackResponse = new ModelThumbFeedbackResponse();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.thumbFeedback(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelThumbFeedbackResponse(resData.data);
        console.log('thumbFeedback', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async exportConversation(params: any): Promise<any> {
    let dataResult: any = null;
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.exportConversation(params);
      const resData = res.data.value;
      // if ($functions.isValidResponse(resData)) {
      if (resData) {
        dataResult = resData;
        console.log('exportConversation', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async getConversationSharedDetail(id: string | number): Promise<ModelConversationDetail> {
    let dataResult: ModelConversationDetail = new ModelConversationDetail();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.getConversationSharedDetail(id);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelConversationDetail(resData.data)
        console.log('getConversationSharedDetail', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
          text: err.message
        });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async getMessageSharedDetail(id: string | number): Promise<ModelSharedConversationMessageDetail> {
    let dataResult: ModelSharedConversationMessageDetail = new ModelSharedConversationMessageDetail();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.getMessageSharedDetail(id);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelSharedConversationMessageDetail(resData.data)
        console.log('getMessageSharedDetail', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
          text: err.message
        });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  } 

  async abbortConversationMessage(params: any): Promise<ModelResponse> {
    let dataResult: ModelResponse = new ModelResponse();
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.abbortConversationMessage(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = resData;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }

  async getConversationAndHistory(params: any): Promise<IModelResponse<ModelConversation[]>> {  
    let dataResult: IModelResponse<ModelConversation[]> = {
      data: [],
    };
    const { $functions } = useNuxtApp()
    try {
      const res = await this.conversationRepository.getConversationAndHistory(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = (resData.data || []).map(e => new ModelConversation(e));
        dataResult.data = dataList;
        dataResult.totalItems = resData.totalItems || 0;
        console.log('getConversationAndHistory', resData.data, dataResult)
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message
      });
      console.log(err, ConversationService.name);
    }
    return dataResult;
  }
}